import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Tabs,
  Table,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  Tag,
  Image,
  Spin,
  Typography,
  Row,
  Col,
  Form,
  Divider,
  Statistic,
  Progress,
  Tooltip,
  Checkbox,
  Modal,
  Alert,
  Segmented,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  FilterOutlined,
  ClearOutlined,
  DownloadOutlined,
  HistoryOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  EnvironmentOutlined,
  FileImageOutlined,
  CheckCircleOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import logo from "../../assets/Logo_Prosperidad_Social.png";

import {
  getAutorizados,
  getSoportesLote,
  actualizarRevision,
  getHistorialRevision,
  precalentarTodasSoportes,
  getEstadoPrecalentado,
  getMunicipiosAutorizados,
} from "../../services/reporteRit/autorizadosEntregasService";
import { esSupervisorDps } from "../../utils/roles";

// Refrescos automáticos seguidos (cada 30s) sin que bajen los pendientes del
// filtro de soportes antes de dejar de reintentar sola (~2 minutos).
const MAX_REFRESCOS_SIN_AVANCE = 4;

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Mismos colores por número de entrega en toda la app (Informe General de
// Entregas usa este mismo esquema: azul / morado / magenta).
const ENCUESTAS = [
  { key: "573", label: "Entregas 1", color: "#1677ff" },
  { key: "667", label: "Entregas 2", color: "#722ed1" },
  { key: "690", label: "Entregas 3", color: "#eb2f96" },
];

const PAGE_SIZE_DEFAULT = 20;

const FILTROS_VACIOS = {
  buscarTitular: "",
  buscarAutorizado: "",
  fechaInicio: null,
  fechaFin: null,
  municipioId: undefined,
  estadoSoportes: undefined,
  revisado: undefined,
};

const OPCIONES_ESTADO_SOPORTES = [
  { value: "completos", label: "Completos" },
  { value: "incompletos", label: "Incompletos" },
  { value: "sin_soportes", label: "Sin soportes" },
];

const OPCIONES_REVISADO = [
  { value: true, label: "Revisados" },
  { value: false, label: "No revisados" },
];

// Selector exclusivo del rol Supervisor DPS. "autorizados" es el comportamiento
// original del reporte (y el que ya tiene la caché de soportes tibia); "todas"
// incluye también las entregas reclamadas por el propio titular (~4-6x más
// registros), por eso no es el valor por defecto.
const OPCIONES_ALCANCE = [
  { value: "autorizados", label: "Autorizadas" },
  { value: "todas", label: "Todas" },
];

const COLOR_QUIEN_RECLAMA = { Autorizado: "orange", Titular: "blue" };

// Cuántas cédulas se validan por lote contra la API externa de soportes (el
// backend rechaza más de 100 por llamada) — se usa tanto al exportar como,
// implícitamente, al paginar la tabla.
const LOTE_VALIDACION_SOPORTES = 100;

const CAMPOS_SOPORTE = ["fotoBeneficio", "docFrontal", "fotoAutorizacion", "firma"];

// Cuenta soportes REALMENTE encontrados (descargados con éxito), no solo los
// que el formulario registró como esperados — un archivo puede estar
// referenciado en la BD y ya no existir en el almacenamiento de RIT.
function contarSoportesReales(soportes) {
  if (!soportes) return 0;
  return CAMPOS_SOPORTE.filter((campo) => soportes[campo]?.estado === "ok").length;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN DE GRUPOS PARA EL EXCEL — mismo estilo que Informe General de
// Entregas (encabezados agrupados a color + logo + resumen por ubicación).
// Solo se incluyen campos que existen y significan algo en este reporte:
// "Quién reclama" no aplica (siempre es "Autorizado" acá, por definición del
// filtro), y no hay un "estado aprobado/rechazado" en este listado.
// ═══════════════════════════════════════════════════════════════════════════

const GRUPOS_EXPORT = [
  {
    titulo: "Información General",
    colorGrupo: "FF1B5E20",
    colorHeader: "FF2E7D32",
    columnas: [
      { label: "Documento titular", campo: (r) => r.identificacion },
      { label: "Municipio", campo: (r) => r.municipio || "—" },
      { label: "Departamento", campo: (r) => r.departamento || "—" },
      {
        label: "Fecha diligenciamiento",
        campo: (r) => (r.fechaDiligenciamiento ? dayjs(r.fechaDiligenciamiento).format("YYYY-MM-DD HH:mm") : ""),
      },
    ],
  },
  {
    titulo: "Titular",
    colorGrupo: "FF0D47A1",
    colorHeader: "FF1565C0",
    columnas: [{ label: "Nombre titular", campo: (r) => r.nombreCompleto || "—" }],
  },
  {
    titulo: "Autorizado",
    colorGrupo: "FFE65100",
    colorHeader: "FFEF6C00",
    columnas: [
      { label: "Documento autorizado", campo: (r) => r.numeroDocumentoAut || "—" },
      { label: "Nombre autorizado", campo: (r) => r.nombreCompletoAut || "—" },
    ],
  },
  {
    titulo: "Soportes",
    colorGrupo: "FF4A148C",
    colorHeader: "FF6A1B9A",
    // imagenesReales se calcula al exportar, verificando cada soporte contra
    // la API externa (no basta con que el formulario haya registrado el
    // nombre del archivo — puede ya no existir).
    // imagenesReales = null solo ocurre en el export de "todas" (rol DPS),
    // que lee la caché en vez de verificar en vivo: registro aún sin verificar.
    columnas: [
      {
        label: "N° soportes verificados",
        campo: (r) =>
          r.imagenesReales === null ? "Pendiente de verificación" : `${r.imagenesReales ?? 0}/${r.imagenesEsperadas}`,
      },
    ],
  },
  {
    titulo: "Auditoría",
    colorGrupo: "FF004D40",
    colorHeader: "FF00695C",
    // revisado/revisadoPor/revisadoFecha vienen ya resueltos por el backend
    // (JOIN contra el estado actual) en la misma consulta de datos.
    columnas: [
      { label: "Revisado", campo: (r) => (r.revisado ? "Sí" : "No") },
      { label: "Revisado por", campo: (r) => r.revisadoPor || "—" },
      {
        label: "Fecha de revisión",
        campo: (r) => (r.revisadoFecha ? dayjs(r.revisadoFecha).format("YYYY-MM-DD HH:mm") : "—"),
      },
    ],
  },
];

// Export de "todas" (rol DPS): mismas columnas + "Quién reclama", que acá sí
// varía (Titular/Autorizado). GRUPOS_EXPORT queda intacto para los demás.
const GRUPOS_EXPORT_TODAS = GRUPOS_EXPORT.map((grupo) =>
  grupo.titulo === "Información General"
    ? {
        ...grupo,
        columnas: [...grupo.columnas, { label: "Quién reclama", campo: (r) => r.quienReclama || "—" }],
      }
    : grupo
);

const totalColumnas = (grupos) => grupos.reduce((s, g) => s + g.columnas.length, 0);

// ── Miniatura de un soporte: mientras se resuelve muestra Spin; si no existe
// (o falló), no muestra nada — nunca un ícono roto ni un placeholder vacío ──
function SoporteThumb({ archivo, cargando, label }) {
  if (cargando) {
    return (
      <div style={{ width: 56, height: 56, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="small" />
      </div>
    );
  }
  if (!archivo || archivo.estado !== "ok" || !archivo.base64) return null;

  return (
    <Image
      src={archivo.base64}
      alt={label}
      width={56}
      height={56}
      style={{ objectFit: "cover", borderRadius: 6, cursor: "pointer" }}
      preview={{ mask: false }}
    />
  );
}

export default function AutorizadosEntregas() {
  const token = useSelector((state) => state.auth.user?.token);
  const esAdmin = useSelector((state) => state.auth.user?.priv_admin);
  const esDps = esSupervisorDps(useSelector((state) => state.auth.user));
  // Marcar/desmarcar "Revisado": admin o Supervisor DPS (el backend lo valida).
  const puedeRevisar = esAdmin || esDps;

  // Solo el rol DPS puede cambiarlo; para cualquier otro siempre es "autorizados".
  const [alcance, setAlcance] = useState("autorizados");
  const verTodas = esDps && alcance === "todas";

  const [idEncuesta, setIdEncuesta] = useState("573");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  const [filtrosDraft, setFiltrosDraft] = useState(FILTROS_VACIOS);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_VACIOS);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [progresoExport, setProgresoExport] = useState(0);

  const [soportesPorCedula, setSoportesPorCedula] = useState({});
  const [cedulasCargando, setCedulasCargando] = useState(new Set());

  const [municipios, setMunicipios] = useState([]);

  const [revisandoIdentificaciones, setRevisandoIdentificaciones] = useState(new Set());
  const [historialModal, setHistorialModal] = useState({ open: false, cargando: false, datos: [], identificacion: null });

  // ── Precalentado de caché (solo admin) — ver handlePrecalentarCache ──
  const [precalentando, setPrecalentando] = useState(false);
  const [verificacionInfo, setVerificacionInfo] = useState(null); // { pendientesVerificar } | null
  const pollPrecalentadoRef = useRef(null);

  // Evita que una respuesta de red atrasada (de una página/pestaña/filtro que
  // el usuario ya abandonó) pise los datos de lo que se está viendo ahora.
  const peticionVigenteRef = useRef(0);

  const entregaActual = ENCUESTAS.find((e) => e.key === idEncuesta);

  // ── Municipios para el filtro (se cargan una sola vez): solo los que
  // tienen cupos > 0 — el catálogo completo traía municipios sin entregas ──
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const data = await getMunicipiosAutorizados(token);
        setMunicipios(
          (data || []).map((m) => ({
            value: m.oid,
            // Con el departamento: hay municipios homónimos (ej. "San Luis").
            label: m.departamento ? `${m.municipio} (${m.departamento})` : m.municipio,
          }))
        );
      } catch (e) {
        console.error(e);
        message.error("Error al cargar los municipios del filtro.");
      }
    })();
  }, [token]);

  const construirParams = useCallback(
    (overrides = {}) => ({
      idEncuesta: Number(idEncuesta),
      page,
      pageSize,
      buscarTitular: filtrosAplicados.buscarTitular || undefined,
      buscarAutorizado: filtrosAplicados.buscarAutorizado || undefined,
      fechaInicio: filtrosAplicados.fechaInicio || undefined,
      fechaFin: filtrosAplicados.fechaFin || undefined,
      municipioId: filtrosAplicados.municipioId || undefined,
      estadoSoportes: filtrosAplicados.estadoSoportes || undefined,
      revisado: filtrosAplicados.revisado,
      alcance: verTodas ? "todas" : undefined,
      ...overrides,
    }),
    [idEncuesta, page, pageSize, filtrosAplicados, verTodas]
  );

  // ── Cargar la página actual (rápido: solo BD, sin tocar la API externa) ──
  const cargarPagina = useCallback(async () => {
    const miPeticion = ++peticionVigenteRef.current;
    setLoading(true);
    try {
      const resp = await getAutorizados(construirParams(), token);
      if (miPeticion !== peticionVigenteRef.current) return; // respuesta obsoleta
      setRows(resp.rows || []);
      setTotal(resp.total || 0);
      setVerificacionInfo(resp.verificacionParcial ? { pendientesVerificar: resp.pendientesVerificar } : null);
    } catch (e) {
      console.error(e);
      if (miPeticion === peticionVigenteRef.current) message.error("Error al cargar los registros.");
    } finally {
      if (miPeticion === peticionVigenteRef.current) setLoading(false);
    }
  }, [construirParams, token]);

  useEffect(() => {
    cargarPagina();
  }, [cargarPagina]);

  // Mientras el filtro de soportes devuelva resultados parciales, se vuelve a
  // consultar sola cada 30s: el backend sigue verificando en segundo plano y
  // cada consulta trae lo que ya quedó en caché, sin que el usuario tenga que
  // pulsar "Buscar" una y otra vez.
  //
  // Si los pendientes dejan de bajar durante varios refrescos seguidos (p. ej.
  // RIT caído), se deja de reintentar sola para no consultar indefinidamente;
  // el usuario puede seguir con "Reintentar".
  const sinAvanceRef = useRef({ consulta: null, pendientes: null, intentos: 0 });
  const [autoRefrescoPausado, setAutoRefrescoPausado] = useState(false);

  useEffect(() => {
    if (!verificacionInfo) {
      sinAvanceRef.current = { consulta: null, pendientes: null, intentos: 0 };
      setAutoRefrescoPausado(false);
      return;
    }
    // Otra consulta (filtros/página/pestaña distintos, o "Reintentar") = se
    // empieza a contar de nuevo; solo cuentan los refrescos de la misma.
    const previo = sinAvanceRef.current.consulta === cargarPagina ? sinAvanceRef.current : { pendientes: null, intentos: 0 };
    const avanzo = previo.pendientes === null || verificacionInfo.pendientesVerificar < previo.pendientes;
    sinAvanceRef.current = {
      consulta:   cargarPagina,
      pendientes: verificacionInfo.pendientesVerificar,
      intentos:   avanzo ? 0 : previo.intentos + 1,
    };
    if (sinAvanceRef.current.intentos >= MAX_REFRESCOS_SIN_AVANCE) {
      setAutoRefrescoPausado(true);
      return;
    }
    setAutoRefrescoPausado(false);
    const timer = setTimeout(cargarPagina, 30_000);
    return () => clearTimeout(timer);
  }, [verificacionInfo, cargarPagina]);

  // ── Traer los soportes SOLO de las cédulas de la página visible, en un
  // único lote (el backend limita la concurrencia contra la API externa) ──
  // Los soportes son por encuesta (la misma cédula puede estar en varias
  // entregas) y por alcance: al cambiar de pestaña o de vista se descartan.
  useEffect(() => {
    setSoportesPorCedula({});
  }, [idEncuesta, verTodas]);

  useEffect(() => {
    if (rows.length === 0) return;
    const miPeticion = peticionVigenteRef.current;
    const visibles = new Set(rows.map((r) => r.identificacion));

    // Solo se conservan las imágenes de la página visible (antes se
    // acumulaban en memoria las de cada página recorrida) y solo se piden las
    // que faltan — así el refresco automático del filtro parcial no vuelve a
    // descargar las mismas imágenes cada 30s.
    setSoportesPorCedula((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([cedula]) => visibles.has(cedula)))
    );
    const identificaciones = [...visibles].filter((cedula) => !soportesPorCedula[cedula]);
    if (identificaciones.length === 0) {
      setCedulasCargando(new Set()); // por si quedó marcada una petición anterior ya descartada
      return;
    }

    setCedulasCargando(new Set(identificaciones));

    (async () => {
      try {
        const soportes = await getSoportesLote(Number(idEncuesta), identificaciones, token, verTodas ? "todas" : undefined);
        if (miPeticion !== peticionVigenteRef.current) return; // ya se cambió de página/pestaña/filtro
        setSoportesPorCedula((prev) => ({ ...prev, ...soportes }));
      } catch (e) {
        console.error(e);
      } finally {
        if (miPeticion === peticionVigenteRef.current) setCedulasCargando(new Set());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, idEncuesta, token]);

  // ── Marcar/desmarcar "Revisado" (admin o Supervisor DPS — el backend igual lo valida).
  // Se refresca la página completa en vez de parchear localmente para que el
  // filtro "Revisado"/total y el revisadoPor/Fecha queden siempre fieles a lo
  // que quedó en BD, sin duplicar esa lógica en el frontend ──
  const handleToggleRevisado = async (row, marcado) => {
    setRevisandoIdentificaciones((prev) => new Set(prev).add(row.identificacion));
    try {
      await actualizarRevision(Number(idEncuesta), row.identificacion, marcado ? "marcar" : "desmarcar", token);
      message.success(marcado ? "Registro marcado como revisado." : "Registro desmarcado.");
      await cargarPagina();
    } catch (e) {
      console.error(e);
      message.error(e?.response?.data?.mensaje || "Error al actualizar la revisión.");
      // 409 = otro usuario ya lo cambió: se recarga para mostrar el estado real.
      if (e?.response?.status === 409) await cargarPagina();
    } finally {
      setRevisandoIdentificaciones((prev) => {
        const next = new Set(prev);
        next.delete(row.identificacion);
        return next;
      });
    }
  };

  const verHistorialRevision = async (identificacion) => {
    setHistorialModal({ open: true, cargando: true, datos: [], identificacion });
    try {
      const historial = await getHistorialRevision(Number(idEncuesta), identificacion, token);
      setHistorialModal({ open: true, cargando: false, datos: historial, identificacion });
    } catch (e) {
      console.error(e);
      message.error("Error al cargar el historial.");
      setHistorialModal({ open: true, cargando: false, datos: [], identificacion });
    }
  };

  // ── Precalentado de caché de soportes (solo admin) — dispara en el backend
  // la verificación de TODOS los registros de la encuesta en segundo plano
  // (ver el filtro "Estado de soportes" más abajo, que es lento la primera
  // vez por esto mismo) y consulta el estado cada 10s hasta que termine ──
  const detenerPollingPrecalentado = () => {
    if (pollPrecalentadoRef.current) {
      clearInterval(pollPrecalentadoRef.current);
      pollPrecalentadoRef.current = null;
    }
  };

  // El intervalo vive varios minutos: si llamara al cargarPagina capturado al
  // crearlo, recargaría con los filtros/página de ESE momento y pisaría lo que
  // el usuario está viendo ahora. Con la ref siempre usa la versión vigente.
  const cargarPaginaRef = useRef(cargarPagina);
  cargarPaginaRef.current = cargarPagina;

  const iniciarPollingPrecalentado = useCallback(() => {
    detenerPollingPrecalentado();
    pollPrecalentadoRef.current = setInterval(async () => {
      try {
        const estado = await getEstadoPrecalentado(Number(idEncuesta), token);
        if (!estado.enCurso) {
          detenerPollingPrecalentado();
          setPrecalentando(false);
          message.success("Precalentado terminado — el filtro \"Estado de soportes\" ya debería responder rápido.");
          cargarPaginaRef.current();
        }
      } catch (e) {
        console.error(e);
      }
    }, 10000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEncuesta, token]);

  // Revisa (una sola vez, al entrar o cambiar de pestaña) si ya hay un
  // precalentado en curso — por el cron nocturno, u otro admin — y si es así
  // engancha el polling para reflejarlo en el botón.
  useEffect(() => {
    detenerPollingPrecalentado();
    setPrecalentando(false);
    if (!esAdmin) return;
    (async () => {
      try {
        const estado = await getEstadoPrecalentado(Number(idEncuesta), token);
        if (estado.enCurso) {
          setPrecalentando(true);
          iniciarPollingPrecalentado();
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return detenerPollingPrecalentado;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEncuesta, esAdmin]);

  const handlePrecalentarCache = async () => {
    setPrecalentando(true);
    try {
      // Precalienta las 3 entregas (573/667/690), una por una, en segundo
      // plano — así, si después cambias de pestaña, esa encuesta ya va
      // quedando tibia en su turno sin tener que repetir el botón por cada una.
      // Si ya había una pasada corriendo (cron nocturno u otro admin), el
      // backend no lanza otra: solo se engancha el polling a la existente.
      const resp = await precalentarTodasSoportes(token);
      message.info(
        resp?.enCurso
          ? resp.mensaje
          : "Precalentado de las 3 entregas iniciado en segundo plano — puede tardar varios minutos si es la primera vez."
      );
      iniciarPollingPrecalentado();
    } catch (e) {
      console.error(e);
      message.error(e?.response?.data?.mensaje || "Error al iniciar el precalentado.");
      setPrecalentando(false);
    }
  };

  // ── Cambiar de pestaña de entrega ──
  const handleCambiarEncuesta = (nuevaKey) => {
    setIdEncuesta(nuevaKey);
    setPage(1);
  };

  // ── Autorizadas / Todas (solo rol DPS) ──
  const handleCambiarAlcance = (nuevoAlcance) => {
    setAlcance(nuevoAlcance);
    setPage(1);
  };

  // ── Filtros ──
  const handleBuscar = () => {
    // Copia nueva a propósito: con el mismo objeto, React no detecta cambio y
    // "Buscar"/"Reintentar" sin tocar filtros no volvía a consultar — justo lo
    // que hace falta para traer lo que la verificación en segundo plano ya
    // dejó en caché.
    setFiltrosAplicados({ ...filtrosDraft });
    setPage(1);
  };

  const handleLimpiarFiltros = () => {
    setFiltrosDraft(FILTROS_VACIOS);
    setFiltrosAplicados(FILTROS_VACIOS);
    setPage(1);
  };

  const hayFiltrosActivos =
    !!filtrosAplicados.buscarTitular ||
    !!filtrosAplicados.buscarAutorizado ||
    !!filtrosAplicados.fechaInicio ||
    !!filtrosAplicados.municipioId ||
    !!filtrosAplicados.estadoSoportes ||
    filtrosAplicados.revisado !== undefined;

  // ── Excel: helpers de estilo (mismo patrón que Informe General de Entregas) ──

  const numToColLetter = (n) => {
    let result = "";
    while (n > 0) {
      const rem = (n - 1) % 26;
      result = String.fromCharCode(65 + rem) + result;
      n = Math.floor((n - 1) / 26);
    }
    return result || "A";
  };

  const aplicarEncabezadoSheet = (worksheet, titulo, totalColumnas, logoImageId) => {
    const colFin = numToColLetter(Math.max(totalColumnas, 4));
    if (logoImageId !== null) {
      worksheet.mergeCells("A1:B3");
      worksheet.addImage(logoImageId, { tl: { col: 0.2, row: 0.5 }, ext: { width: 180, height: 60 } });
    }
    worksheet.mergeCells(`C1:${colFin}2`);
    const titleCell = worksheet.getCell("C1");
    titleCell.value = titulo;
    titleCell.font = { size: 16, bold: true, color: { argb: "FF1B5E20" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    worksheet.mergeCells(`C3:${colFin}3`);
    const dateCell = worksheet.getCell("C3");
    dateCell.value = `Fecha de exportación: ${new Date().toLocaleDateString("es-CO")}`;
    dateCell.alignment = { horizontal: "center" };
    worksheet.addRow([]);
  };

  const bordeCompleto = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };

  // ── Resumen por ubicación al final de la hoja ──
  const agregarResumenPorUbicacion = (worksheet, filas) => {
    const mapa = new Map();
    filas.forEach((r) => {
      const departamento = r.departamento || "—";
      const municipio = r.municipio || "—";
      const clave = `${departamento}|||${municipio}`;
      if (!mapa.has(clave)) mapa.set(clave, { departamento, municipio, total: 0, completos: 0, pendientes: 0 });
      const registro = mapa.get(clave);
      registro.total++;
      // Solo en el export de "todas" (rol DPS): sin verificar todavía — no se
      // cuenta ni como completo ni como incompleto.
      if (r.imagenesReales === null) {
        registro.pendientes++;
        return;
      }
      const esperadas = Number(r.imagenesEsperadas) || 0;
      if (esperadas > 0 && Number(r.imagenesReales ?? 0) >= esperadas) registro.completos++;
    });

    const filasResumen = [...mapa.values()].sort(
      (a, b) => a.departamento.localeCompare(b.departamento) || a.municipio.localeCompare(b.municipio)
    );

    worksheet.addRow([]);
    worksheet.addRow([]);

    const tituloRowNum = worksheet.rowCount + 1;
    worksheet.mergeCells(tituloRowNum, 1, tituloRowNum, 5);
    const tituloCell = worksheet.getCell(tituloRowNum, 1);
    tituloCell.value = "RESUMEN POR UBICACIÓN";
    tituloCell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
    tituloCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF37474F" } };
    tituloCell.alignment = { horizontal: "center", vertical: "middle" };

    const headerRow = worksheet.addRow(["Departamento", "Municipio", "Total", "Completos (verificados)", "Incompletos"]);
    const coloresHeader = ["FF1B5E20", "FF1B5E20", "FF4A148C", "FF2E7D32", "FFC62828"];
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: coloresHeader[colNumber - 1] } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = bordeCompleto;
    });

    let totalGeneral = 0;
    let totalCompletos = 0;
    let totalPendientes = 0;

    filasResumen.forEach((r) => {
      const incompletos = r.total - r.completos - r.pendientes;
      totalGeneral += r.total;
      totalCompletos += r.completos;
      totalPendientes += r.pendientes;

      const row = worksheet.addRow([r.departamento, r.municipio, r.total, r.completos, incompletos]);
      row.eachCell((cell, colNumber) => {
        cell.border = bordeCompleto;
        cell.alignment = colNumber >= 3 ? { horizontal: "center" } : { horizontal: "left" };
      });
    });

    const totalIncompletos = totalGeneral - totalCompletos - totalPendientes;
    const totalRow = worksheet.addRow(["TOTAL GENERAL", "", totalGeneral, totalCompletos, totalIncompletos]);
    worksheet.mergeCells(totalRow.number, 1, totalRow.number, 2);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF37474F" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = { ...bordeCompleto, bottom: { style: "double" } };
    });

    if (totalPendientes > 0) {
      const notaRow = worksheet.addRow([
        `${totalPendientes.toLocaleString("es-CO")} registro(s) pendientes de verificación de soportes: no se cuentan como completos ni incompletos.`,
      ]);
      worksheet.mergeCells(notaRow.number, 1, notaRow.number, 5);
      notaRow.getCell(1).font = { italic: true, color: { argb: "FF8D6E63" } };
    }

    const anchoMinDepto = Math.max(18, ...filasResumen.map((r) => r.departamento.length + 3));
    const anchoMinMunic = Math.max(18, ...filasResumen.map((r) => r.municipio.length + 3));
    [anchoMinDepto, anchoMinMunic, 12, 16, 14].forEach((ancho, i) => {
      const col = worksheet.getColumn(i + 1);
      col.width = Math.max(col.width || 0, ancho);
    });
  };

  // ── Hoja principal con encabezados agrupados a color ──
  const crearHojaAutorizados = (
    workbook,
    filas,
    entrega,
    logoImageId,
    { grupos = GRUPOS_EXPORT, prefijoHoja = "Autorizados", titulo = "SOPORTES AUTORIZADOS" } = {}
  ) => {
    const worksheet = workbook.addWorksheet(`${prefijoHoja} ${entrega.label}`);

    aplicarEncabezadoSheet(worksheet, `${titulo} — ${entrega.label.toUpperCase()}`, totalColumnas(grupos), logoImageId);

    // ── Fila de grupos ──
    const groupRow = worksheet.rowCount + 1;
    let colCursor = 1;
    grupos.forEach((grupo) => {
      const inicio = colCursor;
      const fin = colCursor + grupo.columnas.length - 1;
      worksheet.mergeCells(groupRow, inicio, groupRow, fin);
      const cell = worksheet.getCell(groupRow, inicio);
      cell.value = grupo.titulo.toUpperCase();
      cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: grupo.colorGrupo } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      for (let c = inicio; c <= fin; c++) {
        worksheet.getCell(groupRow, c).border = bordeCompleto;
      }
      colCursor = fin + 1;
    });
    worksheet.getRow(groupRow).height = 22;

    // ── Fila de encabezados de columna ──
    const headerRow = groupRow + 1;
    colCursor = 1;
    grupos.forEach((grupo) => {
      grupo.columnas.forEach((col) => {
        const cell = worksheet.getCell(headerRow, colCursor);
        cell.value = col.label;
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: grupo.colorHeader } };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = bordeCompleto;
        colCursor++;
      });
    });
    worksheet.getRow(headerRow).height = 28;

    // ── Filas de datos ──
    filas.forEach((fila) => {
      const valores = [];
      grupos.forEach((grupo) => grupo.columnas.forEach((col) => valores.push(col.campo(fila))));
      const row = worksheet.addRow(valores);
      row.eachCell((cell) => {
        cell.border = bordeCompleto;
        cell.alignment = { vertical: "middle" };
      });
    });

    // ── Anchos de columna ──
    colCursor = 1;
    grupos.forEach((grupo) => {
      grupo.columnas.forEach((col) => {
        const valores = filas.map((f) => String(col.campo(f) ?? ""));
        const ancho = Math.max(col.label.length, ...valores.map((v) => v.length), 10) + 3;
        worksheet.getColumn(colCursor).width = ancho;
        colCursor++;
      });
    });

    worksheet.views = [{ state: "frozen", xSplit: 0, ySplit: headerRow, showGridLines: true }];

    agregarResumenPorUbicacion(worksheet, filas);

    return worksheet;
  };

  // ── Verifica los soportes reales de TODAS las filas a exportar, en lotes
  // de LOTE_VALIDACION_SOPORTES (el backend rechaza lotes más grandes) y
  // SECUENCIALES (no en paralelo) para no ráfagas contra la API externa de
  // RIT — igual que hace el backend al descargar soportes de una página ──
  const validarSoportesParaExport = useCallback(
    async (idEncuestaNum, filas, onProgresoLote) => {
      const soportesPorCedula = {};
      const totalLotes = Math.ceil(filas.length / LOTE_VALIDACION_SOPORTES);
      for (let i = 0; i < totalLotes; i++) {
        const lote = filas
          .slice(i * LOTE_VALIDACION_SOPORTES, (i + 1) * LOTE_VALIDACION_SOPORTES)
          .map((f) => f.identificacion);
        const resultado = await getSoportesLote(idEncuestaNum, lote, token);
        Object.assign(soportesPorCedula, resultado);
        onProgresoLote?.(i + 1, totalLotes);
      }
      return soportesPorCedula;
    },
    [token]
  );

  // ── Exportar: datos + verificación real de soportes contra la API externa
  // de RIT (todas las filas que cumplan el filtro, no solo la página actual) ──
  const handleExportar = async () => {
    setExportando(true);
    setProgresoExport(10);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Sistema Bodecom";
      workbook.created = new Date();

      let logoImageId = null;
      try {
        const response = await fetch(logo);
        const buffer = await response.arrayBuffer();
        logoImageId = workbook.addImage({ buffer, extension: "png" });
      } catch {
        console.warn("Logo no disponible, se omitirá en el Excel");
      }

      setProgresoExport(20);

      // "Todas" (rol DPS): hasta ~44k filas por entrega. En vez de descargar
      // y mandar al navegador las imágenes de cada registro para contarlas
      // (lo que hace el export de Autorizadas), el backend adjunta el conteo
      // desde la caché de verificación (conVerificacion) en la misma consulta.
      // Lo que aún no esté verificado sale como "Pendiente" y el backend lo
      // deja verificándose en segundo plano para el próximo export.
      const resp = await getAutorizados(
        construirParams({ exportAll: true, page: 1, pageSize: 0, ...(verTodas && { conVerificacion: true }) }),
        token
      );
      const data = resp.rows || [];

      if (data.length === 0) {
        message.warning("No hay registros para exportar con los filtros actuales.");
        return;
      }

      setProgresoExport(35);

      if (!verTodas) {
        const soportesExport = await validarSoportesParaExport(Number(idEncuesta), data, (hecho, totalLotes) => {
          setProgresoExport(35 + Math.round((hecho / totalLotes) * 45)); // 35 -> 80
        });
        data.forEach((fila) => {
          fila.imagenesReales = contarSoportesReales(soportesExport[fila.identificacion]);
        });
      }

      setProgresoExport(85);

      crearHojaAutorizados(
        workbook,
        data,
        entregaActual,
        logoImageId,
        verTodas ? { grupos: GRUPOS_EXPORT_TODAS, prefijoHoja: "Soportes", titulo: "SOPORTES DE ENTREGA" } : undefined
      );

      setProgresoExport(95);

      const periodoLabel = filtrosAplicados.fechaInicio && filtrosAplicados.fechaFin
        ? `_${filtrosAplicados.fechaInicio}_${filtrosAplicados.fechaFin}`
        : "";
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        `${verTodas ? "SoportesEntrega" : "SoportesAutorizados"}_${entregaActual.label.replace(" ", "")}${periodoLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      message.success(`Excel exportado con ${data.length.toLocaleString("es-CO")} registros.`);
      if (resp.pendientesVerificar > 0) {
        message.warning(
          `${resp.pendientesVerificar.toLocaleString("es-CO")} registro(s) salieron como "Pendiente de verificación". Ya se están verificando en segundo plano; vuelve a exportar más tarde para tenerlos completos.`,
          8
        );
      }
    } catch (e) {
      console.error(e);
      message.error("Error al exportar.");
    } finally {
      setExportando(false);
      setProgresoExport(0);
    }
  };

  const columnasSoporte = [
    { titulo: "Foto beneficio", campo: "fotoBeneficio" },
    { titulo: "Doc. frontal", campo: "docFrontal" },
    { titulo: "Foto autorización", campo: "fotoAutorizacion" },
    { titulo: "Firma", campo: "firma" },
  ];

  const columns = [
    {
      title: "Revisado",
      key: "revisado",
      width: 70,
      fixed: "left" ,
      align: "center",
      render: (_, row) => {
        const cargando = revisandoIdentificaciones.has(row.identificacion);
        const tooltipCheck = !puedeRevisar
          ? "Solo un administrador o un Supervisor DPS puede marcar/desmarcar este check"
          : row.revisado
          ? `Revisado por ${row.revisadoPor || "—"}${row.revisadoFecha ? " el " + dayjs(row.revisadoFecha).format("YYYY-MM-DD HH:mm") : ""}`
          : "Marcar como revisado";
        return (
          <Space>
            <Tooltip title={tooltipCheck}>
              <Checkbox
                checked={row.revisado}
                disabled={!puedeRevisar || cargando}
                onChange={(e) => handleToggleRevisado(row, e.target.checked)}
              />
            </Tooltip>
            <Tooltip title="Ver historial de revisión">
              <Button
                type="text"
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => verHistorialRevision(row.identificacion)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    { title: "Documento titular", dataIndex: "identificacion", key: "identificacion", width: 140, fixed: "left" },
    { title: "Nombre titular", dataIndex: "nombreCompleto", key: "nombreCompleto", width: 200, render: (v) => v || "—" },
    ...(esDps
      ? [
          {
            title: "Quién reclama",
            dataIndex: "quienReclama",
            key: "quienReclama",
            width: 120,
            align: "center",
            render: (v) => (v ? <Tag color={COLOR_QUIEN_RECLAMA[v]}>{v}</Tag> : "—"),
          },
        ]
      : []),
    { title: "Documento autorizado", dataIndex: "numeroDocumentoAut", key: "numeroDocumentoAut", width: 150, render: (v) => v || "—" },
    { title: "Nombre autorizado", dataIndex: "nombreCompletoAut", key: "nombreCompletoAut", width: 200, render: (v) => v || "—" },
    { title: "Municipio", dataIndex: "municipio", key: "municipio", width: 140 },
    {
      title: "Fecha",
      dataIndex: "fechaDiligenciamiento",
      key: "fechaDiligenciamiento",
      width: 130,
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "—"),
    },
    {
      title: "N° imágenes",
      key: "imagenesEsperadas",
      width: 110,
      align: "center",
      // Cuenta REAL (soportes verificados/descargados) contra los requeridos
      // por regla (Autorizado 4, Titular 3 — los calcula el backend): falta
      // tanto lo que no vino en la encuesta como lo que no existe en RIT.
      render: (_, row) => {
        if (cedulasCargando.has(row.identificacion)) {
          return <Spin size="small" />;
        }
        const esperadas = row.imagenesEsperadas;
        const reales = contarSoportesReales(soportesPorCedula[row.identificacion]);
        const color = esperadas === 0 ? "red" : reales === esperadas ? "green" : reales > 0 ? "orange" : "red";
        const faltantes = esperadas - reales;
        return (
          <Tooltip
            title={
              faltantes > 0
                ? `Faltan ${faltantes} de ${esperadas} soporte(s) requeridos (no registrados en la encuesta o no encontrados en RIT)`
                : undefined
            }
          >
            <Tag color={color}>{reales}/{esperadas}</Tag>
          </Tooltip>
        );
      },
    },
    ...columnasSoporte.map(({ titulo, campo }) => ({
      title: titulo,
      key: campo,
      width: 90,
      align: "center",
      render: (_, row) =>
        // La autorización solo se exige cuando reclama un Autorizado.
        campo === "fotoAutorizacion" && row.quienReclama !== "Autorizado" ? (
          <Tooltip title="No aplica: la entrega la reclamó el titular">
            <Text type="secondary">N/A</Text>
          </Tooltip>
        ) : (
        <Image.PreviewGroup>
          <SoporteThumb
            archivo={soportesPorCedula[row.identificacion]?.[campo]}
            cargando={cedulasCargando.has(row.identificacion)}
            label={`${titulo} — ${row.identificacion}`}
          />
        </Image.PreviewGroup>
      ),
    })),
    
  ];

  return (
    <Card style={{ margin: 20 }}>
      <Typography.Title level={3}>
        {esDps
          ? `Soportes de Entrega — ${verTodas ? "Todas las entregas" : "Entregas autorizadas"}`
          : "Soportes de Entregas Autorizadas"}
      </Typography.Title>

      {esDps && (
        <Space style={{ marginBottom: 8 }} wrap>
          <Text strong>Mostrar entregas:</Text>
          <Segmented
            options={OPCIONES_ALCANCE}
            value={alcance}
            onChange={handleCambiarAlcance}
            disabled={loading || exportando}
          />
          {verTodas && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Incluye las reclamadas por el titular y por un autorizado.
            </Text>
          )}
        </Space>
      )}

      <Tabs
        activeKey={idEncuesta}
        onChange={handleCambiarEncuesta}
        items={ENCUESTAS.map((e) => ({ key: e.key, label: e.label }))}
      />

      {/* ── Tarjeta de total (mismo lenguaje visual que Informe General de Entregas) ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered style={{ borderLeft: `4px solid ${entregaActual.color}` }}>
            <Statistic
              title={`Total ${entregaActual.label} (${verTodas ? "Todas" : "Autorizado"})`}
              value={total}
              valueStyle={{ color: entregaActual.color }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Acciones ── */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Tooltip title={exportando ? "Generando Excel..." : `Exportar ${total.toLocaleString("es-CO")} registros`}>
          <Button
            icon={exportando ? <DownloadOutlined /> : <FileExcelOutlined />}
            onClick={handleExportar}
            loading={exportando}
            disabled={exportando || total === 0}
            style={{ borderColor: "#217346", color: exportando ? undefined : "#217346" }}
          >
            {exportando ? `Descargando ${progresoExport}%` : "Exportar Excel"}
          </Button>
        </Tooltip>
        <Button icon={<ReloadOutlined />} onClick={cargarPagina} disabled={loading || exportando}>
          Actualizar
        </Button>
      </Space>

      {/* ── Progreso de exportación ── */}
      {exportando && (
        <div style={{ marginBottom: 16, maxWidth: 500 }}>
          <Progress percent={progresoExport} status="active" strokeColor={{ "0%": "#217346", "100%": "#52c41a" }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Preparando archivo Excel, no cierres esta ventana...
          </Text>
        </div>
      )}

      {/* ── Filtros ── */}
      <Card
        size="small"
        style={{ marginBottom: 16, background: "#fafafa" }}
        title={
          <Space>
            <FilterOutlined />
            Filtros
            {hayFiltrosActivos && <Tag color="blue">Filtro activo</Tag>}
          </Space>
        }
        extra={
          <Space>
            {esAdmin && (
              <Tooltip title='Verifica en segundo plano TODOS los soportes de esta encuesta contra RIT, para que el filtro "Estado de soportes" responda rápido después. Puede tardar varios minutos la primera vez.'>
                <Button icon={<ThunderboltOutlined />} onClick={handlePrecalentarCache} loading={precalentando}>
                  {precalentando ? "Precalentando..." : "Precalentar caché"}
                </Button>
              </Tooltip>
            )}
            <Button icon={<ClearOutlined />} onClick={handleLimpiarFiltros} disabled={!hayFiltrosActivos}>
              Limpiar
            </Button>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleBuscar}>
              Buscar
            </Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="Titular" style={{ marginBottom: 8 }}>
                <Input
                  prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="Documento o nombre"
                  value={filtrosDraft.buscarTitular}
                  onChange={(e) => setFiltrosDraft((f) => ({ ...f, buscarTitular: e.target.value }))}
                  onPressEnter={handleBuscar}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="Autorizado" style={{ marginBottom: 8 }}>
                <Input
                  prefix={<UsergroupAddOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="Documento o nombre"
                  value={filtrosDraft.buscarAutorizado}
                  onChange={(e) => setFiltrosDraft((f) => ({ ...f, buscarAutorizado: e.target.value }))}
                  onPressEnter={handleBuscar}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="Período de diligenciamiento" style={{ marginBottom: 8 }}>
                <RangePicker
                  style={{ width: "100%" }}
                  value={
                    filtrosDraft.fechaInicio && filtrosDraft.fechaFin
                      ? [dayjs(filtrosDraft.fechaInicio), dayjs(filtrosDraft.fechaFin)]
                      : null
                  }
                  format="YYYY-MM-DD"
                  placeholder={["Fecha inicio", "Fecha fin"]}
                  onChange={(valores) =>
                    setFiltrosDraft((f) => ({
                      ...f,
                      fechaInicio: valores?.[0] ? valores[0].format("YYYY-MM-DD") : null,
                      fechaFin: valores?.[1] ? valores[1].format("YYYY-MM-DD") : null,
                    }))
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="Municipio" style={{ marginBottom: 8 }}>
                <Select
                  placeholder="Todos los municipios"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  suffixIcon={<EnvironmentOutlined style={{ color: "#bfbfbf" }} />}
                  options={municipios}
                  value={filtrosDraft.municipioId}
                  onChange={(v) => setFiltrosDraft((f) => ({ ...f, municipioId: v }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain style={{ margin: "4px 0 12px" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Filtros de verificación (consultan la API externa de RIT — pueden tardar más)
            </Text>
          </Divider>

          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                label="Estado de soportes"
                tooltip="Verifica los soportes reales de TODOS los registros que cumplan los demás filtros antes de mostrar resultados, en vez de confiar en lo registrado en el formulario."
                style={{ marginBottom: 8 }}
              >
                <Select
                  placeholder="Todos"
                  allowClear
                  style={{ width: "100%" }}
                  suffixIcon={<FileImageOutlined style={{ color: "#bfbfbf" }} />}
                  options={OPCIONES_ESTADO_SOPORTES}
                  value={filtrosDraft.estadoSoportes}
                  onChange={(v) => setFiltrosDraft((f) => ({ ...f, estadoSoportes: v }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="Revisado" style={{ marginBottom: 8 }}>
                <Select
                  placeholder="Todos"
                  allowClear
                  style={{ width: "100%" }}
                  suffixIcon={<CheckCircleOutlined style={{ color: "#bfbfbf" }} />}
                  options={OPCIONES_REVISADO}
                  value={filtrosDraft.revisado}
                  onChange={(v) => setFiltrosDraft((f) => ({ ...f, revisado: v }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {verificacionInfo && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="Resultados parciales del filtro de soportes"
          description={`Aún se están verificando ${verificacionInfo.pendientesVerificar.toLocaleString("es-CO")} registro(s) contra RIT en segundo plano — no están incluidos en este resultado todavía${
            total === 0 ? ", por eso la tabla puede salir vacía aunque sí existan registros con ese estado" : ""
          }. ${
            autoRefrescoPausado
              ? "La verificación no avanzó en los últimos minutos (RIT puede estar lento o caído); pulsa \"Reintentar\" más tarde."
              : "La tabla se actualiza sola cada 30 segundos hasta completar la verificación."
          }`}
          action={
            <Button size="small" onClick={handleBuscar}>
              Reintentar
            </Button>
          }
        />
      )}

      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Text type="secondary">
            Mostrando <Text strong>{total.toLocaleString("es-CO")}</Text> registro{total !== 1 ? "s" : ""}{" "}
            {verTodas ? (
              <>
                de <Text strong>todas</Text> las entregas
              </>
            ) : (
              <>
                con estado <Text strong>"Autorizado"</Text>
              </>
            )}
          </Text>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey="identificacion"
        loading={loading}
        bordered
        size="small"
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
          showTotal: (t, range) => `${range[0]}–${range[1]} de ${t.toLocaleString("es-CO")} registros`,
          onChange: (nuevaPagina, nuevoPageSize) => {
            setPage(nuevaPagina);
            if (nuevoPageSize !== pageSize) setPageSize(nuevoPageSize);
          },
        }}
      />

      <Modal
        title={`Historial de revisión — ${historialModal.identificacion || ""}`}
        open={historialModal.open}
        onCancel={() => setHistorialModal((h) => ({ ...h, open: false }))}
        footer={null}
      >
        {historialModal.cargando ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : historialModal.datos.length === 0 ? (
          <Text type="secondary">Sin movimientos registrados para este documento.</Text>
        ) : (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {historialModal.datos.map((h) => (
              <li key={h.id} style={{ marginBottom: 6 }}>
                <Tag color={h.accion === "MARCAR" ? "green" : "red"}>
                  {h.accion === "MARCAR" ? "Marcado" : "Desmarcado"}
                </Tag>{" "}
                por <Text strong>{h.usuarioNombre}</Text> el {dayjs(h.fecha).format("YYYY-MM-DD HH:mm")}
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </Card>
  );
}
